import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fdt, fdate, inr } from "@/lib/format";
import { Empty } from "@/components/pawkt/Empty";
import { StatusBadge } from "@/components/pawkt/StatusBadge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import {
  Calendar, FileText, CreditCard, MessageSquare, Phone, Scissors, Home, GraduationCap,
  Stethoscope, Syringe, Activity, Lock, Upload, Eye, PawPrint,
} from "lucide-react";
import { toast } from "sonner";

export function SectionCard({ title, icon: Icon, action, children, testid }) {
  return (
    <div className="rounded-xl border border-pawkt-border bg-white p-5 shadow-card" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-pawkt-primary" />}
          <h3 className="font-display text-base font-bold text-pawkt-text">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export function KV({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
      <span className="text-pawkt-text-secondary shrink-0">{label}</span>
      <span className={`text-pawkt-text font-medium text-right ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}

export function CompletionBar({ percent, testid }) {
  return (
    <div className="w-full" data-testid={testid}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-pawkt-text-secondary font-semibold uppercase tracking-wide">Profile completion</span>
        <span className="font-bold text-pawkt-primary-deep">{percent}%</span>
      </div>
      <div className="h-2 rounded-full bg-pawkt-elevated overflow-hidden">
        <div className="h-full rounded-full bg-pawkt-primary transition-all" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

const KIND_ICON = {
  booking: Calendar, invoice: FileText, payment: CreditCard, communication: MessageSquare,
  followup: Phone, grooming: Scissors, boarding: Home, training: GraduationCap,
  vet: Stethoscope, vaccination: Syringe, lifecycle: Activity,
};

export function Timeline({ base, testid }) {
  const [limit, setLimit] = useState(50);
  const { data, isFetching } = useQuery({
    queryKey: ["timeline", base, limit],
    queryFn: async () => (await api.get(`${base}/timeline`, { params: { skip: 0, limit } })).data,
  });
  const items = data?.items || [];
  const total = data?.total || 0;
  if (!items.length) return <Empty title="No activity yet" subtitle="Events appear here as this relationship grows." icon={Activity} />;
  return (
    <div data-testid={testid}>
      <div className="relative pl-6">
        <div className="absolute left-[9px] top-2 bottom-2 w-px bg-pawkt-border" />
        {items.map((e, i) => {
          const Icon = KIND_ICON[e.kind] || Activity;
          return (
            <div key={i} className="relative pb-4" data-testid={`tl-${e.kind}`}>
              <div className={`absolute -left-[19px] top-0.5 h-5 w-5 rounded-full flex items-center justify-center ring-4 ring-white ${e.restricted ? "bg-pawkt-elevated" : "bg-pawkt-primary-soft"}`}>
                {e.restricted ? <Lock className="h-3 w-3 text-pawkt-text-secondary" /> : <Icon className="h-3 w-3 text-pawkt-primary" />}
              </div>
              <div className="text-sm font-semibold text-pawkt-text">{e.title}</div>
              {e.summary && <div className="text-xs text-pawkt-text-secondary">{e.summary}</div>}
              <div className="text-[11px] text-pawkt-text-disabled">{fdt(e.at)}</div>
            </div>
          );
        })}
      </div>
      {total > items.length && (
        <div className="text-center mt-3">
          <Button variant="outline" size="sm" onClick={() => setLimit((l) => l + 50)} disabled={isFetching} data-testid="timeline-more">
            {isFetching ? "Loading…" : `Load more (${total - items.length} older)`}
          </Button>
        </div>
      )}
    </div>
  );
}

const DOC_TYPES = [
  { v: "general", l: "General" }, { v: "other", l: "Other" }, { v: "consent", l: "Consent" },
  { v: "grooming", l: "Grooming" }, { v: "boarding", l: "Boarding" }, { v: "training", l: "Training" },
  { v: "vaccination_certificate", l: "Vaccination Certificate" }, { v: "veterinary", l: "Veterinary" },
  { v: "prescription", l: "Prescription" }, { v: "lab_report", l: "Lab Report" },
  { v: "imaging", l: "Imaging" }, { v: "medical_record", l: "Medical Record" },
];

export function DocumentsPanel({ base, canUpload, testid }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data, refetch } = useQuery({ queryKey: ["docs", base], queryFn: async () => (await api.get(`${base}/documents`)).data });
  const items = data?.items || [];

  async function openDoc(id) {
    try {
      const r = await api.get(`/documents/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErr(e)); }
  }

  async function submit(e) {
    e.preventDefault();
    const f = new FormData(e.target);
    if (!f.get("file") || !f.get("file").name) { toast.error("Choose a file"); return; }
    setBusy(true);
    try {
      await api.post(`${base}/documents`, f, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); setOpen(false); refetch();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  }

  return (
    <SectionCard title="Documents" icon={FileText} testid={testid}
      action={canUpload && <Button size="sm" onClick={() => setOpen(true)} data-testid="doc-upload-btn" className="gap-1.5"><Upload className="h-4 w-4" /> Upload</Button>}>
      {!items.length ? <Empty title="No documents uploaded" subtitle="Vaccination certificates, consents, prescriptions and more." icon={FileText} /> : (
        <div className="divide-y divide-pawkt-border">
          {items.map((d) => (
            <div key={d.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`doc-${d.id}`}>
              <FileText className="h-4 w-4 text-pawkt-text-disabled shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-pawkt-text truncate">{d.label || d.doc_type}</div>
                <div className="text-xs text-pawkt-text-secondary capitalize">{(d.doc_type || "").replace(/_/g, " ")} · {Math.round((d.file_size || 0) / 1024)} KB · {fdate(d.created_at)}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => openDoc(d.id)} data-testid={`doc-open-${d.id}`} className="gap-1"><Eye className="h-4 w-4" /> Open</Button>
            </div>
          ))}
        </div>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent data-testid="doc-upload-sheet">
          <SheetHeader><SheetTitle>Upload document</SheetTitle></SheetHeader>
          <form onSubmit={submit} className="space-y-4 mt-4">
            <div>
              <label className="text-xs font-semibold text-pawkt-text-secondary">Type</label>
              <select name="doc_type" defaultValue="general" data-testid="doc-type-select" className="mt-1 w-full rounded-lg border border-pawkt-border bg-white px-3 py-2 text-sm">
                {DOC_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
              <p className="text-[11px] text-pawkt-text-disabled mt-1">Clinical types require veterinary clinical access.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-pawkt-text-secondary">Label</label>
              <Input name="label" placeholder="e.g. Rabies certificate 2026" className="mt-1" data-testid="doc-label-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-pawkt-text-secondary">File (PDF / JPEG / PNG / WEBP, max 8MB)</label>
              <input type="file" name="file" accept=".pdf,image/jpeg,image/png,image/webp" data-testid="doc-file-input" className="mt-1 w-full text-sm" />
            </div>
            <SheetFooter>
              <Button type="submit" disabled={busy} data-testid="doc-submit-btn">{busy ? "Uploading…" : "Upload"}</Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </SectionCard>
  );
}

export function MoneyRow({ billed, paid, outstanding, avg }) {
  const cell = (l, v, tone) => (
    <div className="rounded-lg bg-pawkt-canvas border border-pawkt-border p-3">
      <div className="text-[11px] font-bold uppercase tracking-wide text-pawkt-text-secondary">{l}</div>
      <div className={`font-display text-lg font-extrabold ${tone}`}>{v}</div>
    </div>
  );
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="money-row">
      {cell("Total billed", inr(billed), "text-pawkt-text")}
      {cell("Total paid", inr(paid), "text-pawkt-success-deep")}
      {cell("Outstanding", inr(outstanding), outstanding > 0 ? "text-pawkt-emergency" : "text-pawkt-text")}
      {cell("Avg transaction", inr(avg), "text-pawkt-text")}
    </div>
  );
}

export function CategorySpend({ data, testid }) {
  const rows = data?.categories || [];
  if (!rows.length) return <Empty title="No billed activity yet" subtitle="Category spend appears once this customer is invoiced." icon={CreditCard} />;
  const max = Math.max(...rows.map((r) => r.billed), 1);
  const tot = [
    ["Billed", data.total_billed, "text-pawkt-text"],
    ["Paid", data.total_paid, "text-pawkt-success-deep"],
    ["Refunded", data.total_refunded, "text-pawkt-emergency"],
    ["Outstanding", data.total_outstanding, data.total_outstanding > 0 ? "text-pawkt-emergency" : "text-pawkt-text"],
  ];
  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="catspend-totals">
        {tot.map(([l, v, tone]) => (
          <div key={l} className="rounded-lg bg-pawkt-canvas border border-pawkt-border p-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-pawkt-text-secondary">{l}</div>
            <div className={`font-display text-lg font-extrabold ${tone}`} data-testid={`catspend-total-${l.toLowerCase()}`}>{inr(v)}</div>
          </div>
        ))}
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.category} data-testid={`catspend-${r.category}`}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="font-semibold text-pawkt-text">{r.label}</span>
              <span className="text-pawkt-text-secondary">
                <span className="font-bold text-pawkt-primary-deep" data-testid={`catspend-mix-${r.category}`}>{r.service_mix_percent}%</span> · {inr(r.billed)}
              </span>
            </div>
            <div className="h-2 rounded-full bg-pawkt-elevated overflow-hidden">
              <div className="h-full rounded-full bg-pawkt-primary" style={{ width: `${Math.max(2, (r.billed / max) * 100)}%` }} />
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[11px] text-pawkt-text-secondary">
              <span>Paid <span className="font-semibold text-pawkt-success-deep">{inr(r.paid)}</span></span>
              <span>Outstanding <span className={`font-semibold ${r.outstanding > 0 ? "text-pawkt-emergency" : "text-pawkt-text"}`}>{inr(r.outstanding)}</span></span>
              {r.refunded > 0 && <span>Refunded <span className="font-semibold text-pawkt-emergency">{inr(r.refunded)}</span></span>}
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-pawkt-text-disabled mt-3" data-testid="catspend-denominator">{data.denominator}</p>
    </div>
  );
}

export function FlagChips({ flags }) {
  const items = flags.filter((f) => f.value);
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-3" data-testid="pet-flags">
      {items.map((f) => (
        <span key={f.label} className="inline-flex items-center gap-1 rounded-full bg-pawkt-coral text-pawkt-emergency px-2.5 py-0.5 text-xs font-bold">
          {f.label}: {f.value}
        </span>
      ))}
    </div>
  );
}

export { PawPrint };
